import React, { FC } from 'react';
import { twMerge } from 'tailwind-merge';
import classnames from 'classnames';
import {
  BinIcon,
  EditIconBlue,
  ExpandImageIcon,
  InvoiceSettingsIcon,
  JobTimelineNoteIcon,
  VerticalListActionsIcon
} from 'assets/icons';
import { CustomPopover } from 'components';
import { EstimateTimelineEvent, EstimateTimelineEventType } from '../estimates.model';
import { selectUserDetails } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';

interface EstimateTimelineSectionProps {
  estimateTimeline: EstimateTimelineEvent[];
  limit?: number;
  onEditNoteClick: (event: EstimateTimelineEvent) => void;
  onDeleteNoteClick: (event: EstimateTimelineEvent) => void;
  hasImageViewer?: boolean;
  showPreview?: (index: number, event: EstimateTimelineEvent) => void;
  timelineId: string;
  currentActionsPopoverId: number;
  setCurrentActionsPopoverId: (noteId: number) => void;
  isEditAllowed: boolean;
}

const EstimateTimelineSection: FC<EstimateTimelineSectionProps> = ({
  estimateTimeline,
  limit,
  onEditNoteClick,
  onDeleteNoteClick,
  hasImageViewer,
  showPreview,
  timelineId,
  currentActionsPopoverId,
  setCurrentActionsPopoverId,
  isEditAllowed
}) => {
  const currentUser = useAppSelector(selectUserDetails);

  const getTimelineIcon = (type: string) => {
    switch (type) {
      case EstimateTimelineEventType.Action:
        return <InvoiceSettingsIcon className="path-stroke-current h-[18px] w-[18px] shrink-0 text-tertiaryText" />;
      case EstimateTimelineEventType.Note:
      case EstimateTimelineEventType.EstimateNote:
        return <JobTimelineNoteIcon className="shrink-0" />;
      default:
        return <InvoiceSettingsIcon className="path-stroke-current h-[18px] w-[18px] shrink-0 text-tertiaryText" />;
    }
  };

  const isListItemActionsVisible = (noteId: number) => {
    return noteId === currentActionsPopoverId;
  };

  const onCloseListItemActionsPopover = (noteId: number) => {
    if (currentActionsPopoverId === noteId) {
      setCurrentActionsPopoverId(null);
    }
  };

  const onToggleListItemActions = (noteId: number) => {
    if (noteId === currentActionsPopoverId) {
      setCurrentActionsPopoverId(null);
    } else {
      setCurrentActionsPopoverId(noteId);
    }
  };

  const showPopoverMenu = (estimateTimelineEvent: EstimateTimelineEvent) => {
    return (
      (estimateTimelineEvent.type === EstimateTimelineEventType.Note ||
        (estimateTimelineEvent.type === EstimateTimelineEventType.EstimateNote && isEditAllowed)) &&
      estimateTimelineEvent.merchantId === currentUser.id
    );
  };

  // eslint-disable-next-line react/no-multi-comp
  const ListActionsAnchor: React.FC<{ isPanelOpen: boolean }> = ({ isPanelOpen }) => {
    return (
      <div id="estimates-timeline-notes-more-options">
        <VerticalListActionsIcon
          height={16}
          width={4}
          className={twMerge(
            'mx-2 shrink-0 cursor-pointer',
            classnames({
              'fill-current text-secondary': isPanelOpen
            })
          )}
        />
      </div>
    );
  };

  // eslint-disable-next-line react/no-multi-comp
  const ListItemActionsMenu: FC<{ timelineEvent: EstimateTimelineEvent }> = ({ timelineEvent }) => {
    return (
      <div className="space-y-3 text-sbase font-semibold text-primaryText">
        <div
          id={`${timelineId}-3dot-editnote`}
          role="presentation"
          onClick={e => {
            e.stopPropagation();
            onEditNoteClick(timelineEvent);
          }}
          className="flex cursor-pointer flex-row items-center gap-2.5">
          <EditIconBlue height={16} width={16} />
          <div>Edit note</div>
        </div>
        <div
          id={`${timelineId}-3dot-delete-note`}
          onClick={() => onDeleteNoteClick(timelineEvent)}
          className="flex cursor-pointer flex-row items-center gap-2.5">
          <BinIcon />
          <div>Delete note</div>
        </div>
      </div>
    );
  };

  return (
    <div className="customNormalScroll flex grow flex-col overflow-y-auto p-4 pb-5">
      {estimateTimeline?.map(
        (estimateTimelineEvent, index) =>
          (limit ? index < limit : true) ? (
            <div key={`estimate-timeline-${index + 1}`} className="flex gap-x-2.5">
              <div className="flex flex-col items-center">
                {getTimelineIcon(estimateTimelineEvent.type)}
                {(limit && limit < estimateTimeline.length
                  ? index !== limit - 1
                  : index !== estimateTimeline.length - 1) && <div className="h-full w-px bg-borderGray"></div>}
              </div>
              <div
                className={`w-full ${(limit ? index !== limit - 1 : index !== estimateTimeline.length - 1) && 'pb-6'}`}>
                <div className="flex w-full items-center justify-between">
                  <div className="text-17px font-semibold text-primaryText">{estimateTimelineEvent.title}</div>
                  {showPopoverMenu(estimateTimelineEvent) && (
                    <CustomPopover
                      anchorComponentId="estimate-notes-anchor"
                      showArrow={true}
                      arrowOffset="right-3"
                      minWidth="min-w-[150px]"
                      highlightAnchor={false}
                      anchorComponent={ListActionsAnchor}
                      zIndex="z-[101]"
                      offset="-right-3"
                      margin="mt-1"
                      onClosePopover={() => onCloseListItemActionsPopover(estimateTimelineEvent.noteId)}
                      show={isListItemActionsVisible(estimateTimelineEvent.noteId)}
                      onTogglePopover={() => onToggleListItemActions(estimateTimelineEvent.noteId)}>
                      {<ListItemActionsMenu timelineEvent={estimateTimelineEvent} />}
                    </CustomPopover>
                  )}
                </div>
                {estimateTimelineEvent.subTitle && (
                  <div
                    className="mt-2.5 max-w-[260px] whitespace-pre-wrap break-words text-sbase font-medium 
                              text-primaryText">
                    {estimateTimelineEvent.subTitle}
                  </div>
                )}
                {estimateTimelineEvent.attachments?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-4">
                    {estimateTimelineEvent.attachments.map(
                      (attachment, attachmentIndex) =>
                        attachment.urlLink ? (
                          <div
                            key={`estimate-event-${index}-attachment-${attachmentIndex}`}
                            className="relative h-[60px] max-h-[60px] w-[60px]">
                            <img
                              src={attachment.urlLink}
                              height={60}
                              width={60}
                              className="h-[60px] max-h-[60px] w-[60px] rounded object-cover"
                            />
                            {hasImageViewer && (
                              <ExpandImageIcon
                                className="absolute top-1 right-[3px] cursor-pointer"
                                onClick={() => showPreview(attachmentIndex, estimateTimelineEvent)}
                              />
                            )}
                          </div>
                        ) : null
                      // eslint-disable-next-line function-paren-newline
                    )}
                  </div>
                )}
                <div className="mt-2 text-sbase text-accentText">{estimateTimelineEvent.subText}</div>
              </div>
            </div>
          ) : null
        // eslint-disable-next-line function-paren-newline
      )}
    </div>
  );
};

export default EstimateTimelineSection;
